// =============================================================================
// ServicesScreen — /assets/services — the depot-rotation queue (mig 357,
// T-ASSET-LIFECYCLE item 1, from James's 6 Sep assets curation session).
//
// "A services screen maybe needed with more nuance" + the intended rhythm:
// "cycle vehicles back through the depot, complete due test and tag and
// rigging/harness inspections, re-tag, repack, return them to work."
//
// This screen ANSWERS "what's due and what rotates through the depot next".
// It deliberately RECORDS NOTHING itself — every row links into the flow
// that already owns that record: rigging → /inspections/new (preloaded),
// power tool T&T → the tool's test-tag screen, vehicle dates → the vehicle
// detail. One read over v_asset_services_due; overdue red, due-soon amber,
// never-recorded flagged (session 1: "compliance depends on crews completing
// the inspections" — the gap IS the finding).
//
// 8 Oct 2026 (mig 431) — ladder inspections and test-equipment calibration
// join the queue. Both route to their tab on /assets, where they are recorded.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock, HardHat, Zap, Truck, AlertTriangle, ShieldCheck, Search, Moon, AlignJustify, Gauge,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useStickyState } from "@/lib/stickyState";   // 29 Oct 2026 — filters survive back / refresh / rotate
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, KPI } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

interface DueRow {
  kind: "rigging" | "power_tool" | "vehicle" | "ladder" | "test_equipment";
  item_id: string;
  code: string;
  display_name: string | null;
  kit_name: string | null;
  assigned_to_name: string | null;
  due_type: "rigging_inspection" | "test_and_tag" | "vehicle_service" | "rego" | "ctp" | "ladder_inspection" | "calibration";
  due_date: string | null;
  urgency: "overdue" | "due_soon" | "current" | "never_recorded";
}

const DUE_LABEL: Record<DueRow["due_type"], string> = {
  rigging_inspection: "Rigging inspection",
  test_and_tag:       "Test & tag",
  vehicle_service:    "Service",
  rego:               "Rego",
  ctp:                "CTP",
  ladder_inspection:  "Ladder inspection",
  calibration:        "Calibration",
};

const KIND_ICON = { rigging: HardHat, power_tool: Zap, vehicle: Truck, ladder: AlignJustify, test_equipment: Gauge } as const;

const URGENCY_META: Record<DueRow["urgency"], { label: string; tone: "expired" | "pending" | "approved" | "warning"; rank: number }> = {
  overdue:        { label: "overdue",       tone: "expired",  rank: 0 },
  never_recorded: { label: "never recorded", tone: "warning", rank: 1 },
  due_soon:       { label: "due soon",      tone: "pending",  rank: 2 },
  current:        { label: "current",       tone: "approved", rank: 3 },
};

function daysFromToday(d: string | null): number | null {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(d + "T00:00:00").getTime() - today.getTime()) / 86400000);
}

// Where recording actually happens — this screen never records, it routes.
function actionFor(r: DueRow): { to: string; label: string } {
  if (r.kind === "rigging")    return { to: `/inspections/new?item=${r.item_id}&kind=rigging`, label: "Inspect" };
  if (r.kind === "power_tool") return { to: `/assets/power-tools/${r.item_id}/test-tag`,       label: "Test & tag" };
  if (r.kind === "ladder")         return { to: "/assets?tab=ladders", label: "Inspect" };
  if (r.kind === "test_equipment") return { to: "/assets?tab=eq_test", label: "Update" };
  return { to: `/assets/vehicles/${r.item_id}`, label: "Update" };
}

// The item's record of truth on the existing registers — clicking the name
// lands there, so this queue links into the registers rather than doubling them.
function detailFor(r: DueRow): string {
  if (r.kind === "rigging")    return `/assets/rigging/${r.item_id}`;
  if (r.kind === "power_tool") return `/assets/power-tools/${r.item_id}`;
  if (r.kind === "ladder")         return "/assets?tab=ladders";
  if (r.kind === "test_equipment") return "/assets?tab=eq_test";
  return `/assets/vehicles/${r.item_id}`;
}

export default function ServicesScreen() {
  const user = useRequireAuth();
  const [kindFilter, setKindFilter] = useStickyState<"all" | DueRow["kind"]>("services:kind", "all");
  const [showCurrent, setShowCurrent] = useStickyState("services:current", false);
  const [groupByWho, setGroupByWho] = useStickyState("services:group", false);
  const [search, setSearch] = useStickyState("services:search", "");

  const { data: rows = [], isLoading, isError, error } = useQuery({
    queryKey: ["asset_services_due"],
    queryFn: async (): Promise<DueRow[]> => {
      // unknown-first: database.types.ts predates v_asset_services_due (mig 357)
      const { data, error } = await (supabase as any)
        .from("v_asset_services_due")
        .select("*");
      if (error) throw error;
      return (data ?? []) as DueRow[];
    },
  });

  const kpis = useMemo(() => ({
    overdue: rows.filter(r => r.urgency === "overdue").length,
    dueSoon: rows.filter(r => r.urgency === "due_soon").length,
    never:   rows.filter(r => r.urgency === "never_recorded").length,
    current: rows.filter(r => r.urgency === "current").length,
  }), [rows]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(r => kindFilter === "all" || r.kind === kindFilter)
      .filter(r => showCurrent || r.urgency !== "current")
      .filter(r => !q || [r.code, r.display_name, r.assigned_to_name, r.kit_name]
        .some(x => (x ?? "").toLowerCase().includes(q)))
      .sort((a, b) =>
        URGENCY_META[a.urgency].rank - URGENCY_META[b.urgency].rank
        || (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999")
        || a.code.localeCompare(b.code));
  }, [rows, kindFilter, showCurrent, search]);

  // "Group by who" clusters the rotation work: a vehicle's own rows sit under
  // its code; gear clusters under its assigned person or kit — the closest
  // honest grouping the data supports (tools/rigging attach to PEOPLE and
  // KITS in Ops, not to vehicles).
  const groups = useMemo(() => {
    if (!groupByWho) return [{ key: "", rows: shown }];
    const m = new Map<string, DueRow[]>();
    for (const r of shown) {
      const key = r.kind === "vehicle"
        ? `Vehicle · ${r.code}`
        : (r.assigned_to_name ?? r.kit_name ?? "Unassigned / pool");
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    }
    return [...m.entries()]
      .sort((a, b) => Math.min(...a[1].map(r => URGENCY_META[r.urgency].rank))
                    - Math.min(...b[1].map(r => URGENCY_META[r.urgency].rank)))
      .map(([key, rs]) => ({ key, rows: rs }));
  }, [shown, groupByWho]);

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return <div className="p-6"><EmptyState icon={CalendarClock} title="Managers only" description="Services due is a depot-planning surface." /></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-16">
      <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
              Depot rotation · everything with a due date, one queue
            </div>
          </div>
          <h1 className="text-[24px] font-semibold tracking-tight">Services due</h1>
          <div className="text-text-muted text-[12px] mt-1">
            Rigging inspections, test &amp; tag, vehicle service, rego and CTP. Recording happens in the
            usual flows — this is the list of what to rotate through the depot next.
          </div>
        </div>
        {/* The rhythm: something's due → book the vehicle a depot day (mig 356)
            on the schedule, do the lot, re-tag, repack. */}
        <Link to="/schedule">
          <Button size="sm" variant="secondary" icon={Moon}>Book a depot day →</Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Overdue" value={kpis.overdue} icon={AlertTriangle} accent={kpis.overdue > 0} />
        <KPI label="Due ≤ 30 days" value={kpis.dueSoon} icon={CalendarClock} accent={kpis.dueSoon > 0} />
        <KPI label="Never recorded" value={kpis.never} icon={AlertTriangle} accent={kpis.never > 0} />
        <KPI label="Current" value={kpis.current} icon={ShieldCheck} />
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {([["all", "All"], ["rigging", "Rigging"], ["power_tool", "Power tools"], ["vehicle", "Vehicles"], ["ladder", "Ladders"], ["test_equipment", "Calibration"]] as const).map(([v, label]) => (
          <button key={v} type="button" onClick={() => setKindFilter(v)}
            className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
              kindFilter === v ? "bg-accent/15 text-accent border-accent/40" : "bg-ink-raised text-text-muted border-line"}`}>
            {label}
          </button>
        ))}
        <label className="inline-flex items-center gap-1.5 text-[12px] text-text-muted cursor-pointer select-none ml-1">
          <input type="checkbox" className="accent-accent" checked={showCurrent} onChange={e => setShowCurrent(e.target.checked)} />
          Show current too
        </label>
        <label className="inline-flex items-center gap-1.5 text-[12px] text-text-muted cursor-pointer select-none">
          <input type="checkbox" className="accent-accent" checked={groupByWho} onChange={e => setGroupByWho(e.target.checked)} />
          Group by vehicle / person
        </label>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 min-w-[160px] max-w-xs ml-auto">
          <Search size={13} className="text-text-muted" />
          <input placeholder="Search code, name, person…" value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[12px] flex-1" />
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : isError ? (
        // A failed read must never masquerade as "nothing due" — e.g. the
        // v_asset_services_due view missing because mig 357 hasn't been pushed.
        <Card><EmptyState icon={AlertTriangle} title="Couldn't load the due list"
          description={`${(error as Error)?.message ?? "Query failed"} — if this mentions v_asset_services_due, migration 357 hasn't been applied to this database yet.`} /></Card>
      ) : shown.length === 0 ? (
        <Card><EmptyState icon={ShieldCheck} title="Nothing due"
          description={
            rows.length === 0
              ? "No rigging, power tools or vehicles with due dates on the register yet — rows appear as gear is registered and dates are entered."
              : !showCurrent && kpis.current > 0
                ? `Nothing overdue or due within 30 days. ${kpis.current} current item${kpis.current === 1 ? " is" : "s are"} hidden — tick "Show current too" to see them.`
                : "Nothing on the register matches."
          } /></Card>
      ) : (
        <div className="space-y-4">
          {groups.map(g => (
            <div key={g.key || "flat"}>
              {g.key && (
                <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-1.5 mt-2">
                  {g.key} <span className="text-text-dim normal-case">· {g.rows.length} due</span>
                </div>
              )}
              <Card>
                <div className="divide-y divide-line/40">
                  {g.rows.map(r => {
                    const Icon = KIND_ICON[r.kind];
                    const m = URGENCY_META[r.urgency];
                    const days = daysFromToday(r.due_date);
                    const act = actionFor(r);
                    return (
                      <div key={`${r.kind}-${r.item_id}-${r.due_type}`} className="flex items-center gap-3 px-4 py-2.5">
                        <Icon size={15} className="text-text-muted flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link to={detailFor(r)} className="flex items-center gap-2 min-w-0 hover:underline">
                              <span className="font-mono text-[11px] text-accent">{r.code}</span>
                              <span className="text-[13px] text-text truncate">{r.display_name ?? "—"}</span>
                            </Link>
                            <Pill label={DUE_LABEL[r.due_type]} tone="bid" small />
                          </div>
                          <div className="text-[11px] text-text-muted mt-0.5">
                            {!groupByWho && (r.assigned_to_name ?? r.kit_name ?? "Unassigned")}
                            {!groupByWho && " · "}
                            {r.due_date
                              ? <>{fmtDate(r.due_date)}{days != null && (
                                  <span className={days < 0 ? "text-danger" : days <= 30 ? "text-warn" : "text-text-dim"}>
                                    {" "}({days < 0 ? `${Math.abs(days)}d over` : days === 0 ? "today" : `in ${days}d`})
                                  </span>)}</>
                              : "no record on file"}
                          </div>
                        </div>
                        <Pill label={m.label} tone={m.tone} small />
                        <Link to={act.to}
                          className="text-[12px] text-accent hover:underline flex-shrink-0">{act.label} →</Link>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
